import { FormField } from '@/components/form-section'
import SenderDepartmentSelect from '@/components/sales/SenderDepartmentSelect'
import WhatsappTemplateConfig from '@/components/sales/WhatsappTemplateConfig'
import { UPDATE_DEAL_FIELD_OPTIONS, UPDATE_LEAD_FIELD_OPTIONS } from '@/lib/api/sales/automations-api'
import { LEAD_LIFECYCLE_LABELS, LEAD_TEMPERATURE_LABELS } from '@/lib/api/sales/leads-api'
import type { SalesFlowNode } from '@/lib/api/sales/flows-api'
import type { SalesFlowGraphIssue } from '@mediardex/contracts'
import { MdClose, MdContentCopy, MdDelete } from 'react-icons/md'
import { FLOW_GROUP_STYLE, FLOW_NODE_META } from '../flow-meta'
import type { FlowRefData } from '../use-flow-ref-data'
import ConditionsEditor from './conditions-editor'
import DurationInput from './duration-input'
import { TASK_TYPE_LABELS } from '@/pages/admin/atendimento/agenda/utils/status-colors'
import RecipientConfig, { isTeamRecipient, UserMultiSelect } from './recipient-config'

const TEMPLATE_VARIABLES = ['{{lead.name}}', '{{lead.phoneNumber}}', '{{deal.title}}', '{{deal.stage}}', '{{company.name}}', '{{assignedUser.name}}']

function VariableChips({ onInsert }: { onInsert: (variable: string) => void }) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {TEMPLATE_VARIABLES.map(variable => (
                <button key={variable} type="button" className="badge badge-outline badge-sm cursor-pointer hover:badge-primary" onClick={() => onInsert(variable)}>
                    {variable}
                </button>
            ))}
        </div>
    )
}

function PipelineStageSelect({
    refData,
    pipelineId,
    stageId,
    onChange,
    idPrefix,
}: {
    refData: FlowRefData
    pipelineId?: number | null
    stageId?: number | null
    onChange: (pipelineId: number | null, stageId: number | null) => void
    idPrefix: string
}) {
    const pipeline = refData.pipelines.find(p => p.id === Number(pipelineId))
    return (
        <>
            <FormField label="Funil" htmlFor={`${idPrefix}-pipeline`} required>
                <select
                    id={`${idPrefix}-pipeline`}
                    className="select select-bordered select-sm w-full"
                    value={pipelineId ?? ''}
                    onChange={e => onChange(e.target.value ? Number(e.target.value) : null, null)}
                >
                    <option value="">Selecione o funil</option>
                    {refData.pipelines.map(p => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                            {p.company?.name ? ` (${p.company.name})` : ''}
                        </option>
                    ))}
                </select>
            </FormField>
            <FormField label="Etapa" htmlFor={`${idPrefix}-stage`} required>
                <select
                    id={`${idPrefix}-stage`}
                    className="select select-bordered select-sm w-full"
                    value={stageId ?? ''}
                    disabled={!pipeline}
                    onChange={e => onChange(pipelineId ?? null, e.target.value ? Number(e.target.value) : null)}
                >
                    <option value="">{pipeline ? 'Selecione a etapa' : 'Escolha o funil primeiro'}</option>
                    {(pipeline?.stages ?? []).map(stage => (
                        <option key={stage.id} value={stage.id}>
                            {stage.name}
                        </option>
                    ))}
                </select>
            </FormField>
        </>
    )
}

/**
 * Configuração do passo selecionado. Tudo que o passo guarda vai no `config`
 * (formato em @mediardex/contracts, SalesFlowNodeConfigMap).
 */
export default function NodeConfigPanel({
    node,
    nodes,
    refData,
    issues,
    flowId,
    onChange,
    onDelete,
    onDuplicate,
    onClose,
}: {
    node: SalesFlowNode
    nodes: SalesFlowNode[]
    refData: FlowRefData
    issues: SalesFlowGraphIssue[]
    flowId: number
    onChange: (config: Record<string, any>) => void
    onDelete: () => void
    onDuplicate: () => void
    onClose: () => void
}) {
    const meta = FLOW_NODE_META[node.type]
    const style = FLOW_GROUP_STYLE[meta.group]
    const config = node.config ?? {}
    const set = (patch: Record<string, any>) => onChange({ ...config, ...patch })
    const id = (field: string) => `node-${node.key}-${field}`

    const textArea = (field: string, label: string, placeholder: string, rows = 5, withVariables = true) => (
        <FormField label={label} htmlFor={id(field)} required>
            <textarea
                id={id(field)}
                className="textarea textarea-bordered w-full text-sm leading-relaxed"
                rows={rows}
                placeholder={placeholder}
                value={config[field] ?? ''}
                onChange={e => set({ [field]: e.target.value })}
            />
            {withVariables && <VariableChips onInsert={variable => set({ [field]: `${config[field] ?? ''}${variable}` })} />}
        </FormField>
    )

    // Envio para a equipe: o setor vazio cai no de notificações, não na conversa do lead.
    const teamRecipient = isTeamRecipient(config.recipient)
    const chosenDepartment = refData.departments.find(d => d.id === Number(config.departmentId))
    const teamTextOnOfficialApi = node.type === 'SEND_WHATSAPP' && teamRecipient && chosenDepartment?.whatsappProvider === 'WHATSAPP_API'
    const notifyUserIds: number[] = config.userIds?.length ? config.userIds : config.userId ? [Number(config.userId)] : []

    const senderDepartmentSelect = (leadHint: string) => (
        <SenderDepartmentSelect
            value={config.departmentId}
            onChange={departmentId => set({ departmentId })}
            emptyLabel="Padrão do fluxo"
            emptyHint={
                teamRecipient
                    ? `Usa o setor padrão do fluxo e, sem ele, o setor de notificações.${node.type === 'SEND_WHATSAPP' ? ' Texto livre para a equipe só sai por setor WhatsApp Web.' : ''}`
                    : leadHint
            }
        />
    )

    const body = () => {
        switch (node.type) {
            case 'START':
                return <p className="text-sm text-base-content/60">O início não tem configuração: os gatilhos ficam no painel à esquerda.</p>

            case 'SEND_WHATSAPP':
                return (
                    <>
                        <RecipientConfig idPrefix={id('recipient')} channel="phone" value={config.recipient} users={refData.users} onChange={recipient => set({ recipient })} />
                        <FormField label="Setor de envio" htmlFor={id('department')}>
                            {senderDepartmentSelect('Usa o setor padrão do fluxo e, sem ele, o setor da conversa do lead. Sem nenhum dos dois, não envia.')}
                        </FormField>
                        {teamTextOnOfficialApi && (
                            <div role="alert" className="alert alert-warning py-2 text-xs leading-relaxed">
                                Pela API oficial, a Meta só entrega texto livre a quem escreveu para o número nas últimas 24 horas. Para avisar a equipe por esse setor, use o passo "Enviar template" ou um setor WhatsApp Web.
                            </div>
                        )}
                        {textArea('content', 'Mensagem', teamRecipient ? 'Novo lead no funil: {{lead.name}}, {{lead.phoneNumber}}' : 'Olá {{lead.name}}, ...')}
                    </>
                )

            case 'SEND_TEMPLATE':
                return (
                    <>
                        <RecipientConfig idPrefix={id('recipient')} channel="phone" value={config.recipient} users={refData.users} onChange={recipient => set({ recipient })} />
                        <FormField label="Setor de envio" htmlFor={id('department')}>
                            {senderDepartmentSelect('Usa o setor padrão do fluxo e, sem ele, o setor da conversa do lead.')}
                        </FormField>
                        <WhatsappTemplateConfig
                            size="sm"
                            departmentId={config.departmentId ?? null}
                            templateId={config.templateId}
                            templateParams={config.templateParams}
                            onChange={(templateId, templateParams) => set({ templateId, templateParams })}
                        />
                    </>
                )

            case 'SEND_EMAIL':
                return (
                    <>
                        <RecipientConfig idPrefix={id('recipient')} channel="email" value={config.recipient} users={refData.users} onChange={recipient => set({ recipient })} />
                        <FormField label="Assunto" htmlFor={id('subject')} required>
                            <input id={id('subject')} className="input input-bordered input-sm w-full" value={config.subject ?? ''} onChange={e => set({ subject: e.target.value })} />
                        </FormField>
                        {textArea('content', 'Conteúdo', 'Texto do e-mail', 7)}
                    </>
                )

            case 'NOTIFY_USER':
                return (
                    <>
                        <FormField label="Quem recebe" hint="Sem ninguém marcado, recebe o responsável pelo lead.">
                            <UserMultiSelect
                                users={refData.users}
                                selected={notifyUserIds}
                                onChange={userIds => set({ userIds, userId: null })}
                            />
                        </FormField>
                        <FormField label="Título" htmlFor={id('title')} optional>
                            <input id={id('title')} className="input input-bordered input-sm w-full" value={config.title ?? ''} onChange={e => set({ title: e.target.value })} />
                        </FormField>
                        {textArea('message', 'Mensagem', 'O que a equipe precisa saber', 4)}
                    </>
                )

            case 'PAUSE':
                return (
                    <>
                        <FormField label="Tempo de espera" htmlFor={id('timeout')} required hint="Passado esse tempo, o fluxo segue pela saída Tempo esgotado.">
                            <DurationInput id={id('timeout')} value={config.timeoutMinutes} min={1} onChange={timeoutMinutes => set({ timeoutMinutes })} />
                        </FormField>
                        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-base-300 p-3">
                            <input type="checkbox" className="toggle toggle-primary toggle-sm mt-0.5" checked={!!config.waitForReply} onChange={e => set({ waitForReply: e.target.checked })} />
                            <span>
                                <span className="block text-sm font-medium">Parar de esperar se o contato responder</span>
                                <span className="mt-0.5 block text-xs text-base-content/50">Segue pela saída Respondeu assim que chegar mensagem dele.</span>
                            </span>
                        </label>
                    </>
                )

            case 'CONDITION':
                return (
                    <FormField label="Condições" hint="Verdadeiro segue por Sim; falso, por Não.">
                        <ConditionsEditor
                            conditions={config.conditions ?? []}
                            logic={config.logic ?? 'AND'}
                            emptyText="Sem condições: segue sempre por Sim."
                            onChange={(conditions, logic) => set({ conditions, logic })}
                        />
                    </FormField>
                )

            case 'GO_TO':
                return (
                    <FormField label="Ir para o passo" htmlFor={id('target')} required hint="Evita repetir o mesmo trecho em dois ramos.">
                        <select id={id('target')} className="select select-bordered select-sm w-full" value={config.targetKey ?? ''} onChange={e => set({ targetKey: e.target.value })}>
                            <option value="">Selecione</option>
                            {nodes
                                .filter(n => n.key !== node.key && n.type !== 'START')
                                .map(n => (
                                    <option key={n.key} value={n.key}>
                                        {FLOW_NODE_META[n.type].label} ({n.key})
                                    </option>
                                ))}
                        </select>
                    </FormField>
                )

            case 'MOVE_STAGE': {
                const byName = config.stageName != null
                // Nomes de etapa de todos os funis, para sugerir no modo "pelo nome".
                const stageNames = Array.from(
                    new Set(refData.pipelines.flatMap(p => (p.stages ?? []).map(stage => stage.name))),
                ).sort((a, b) => a.localeCompare(b, 'pt-BR'))
                return (
                    <>
                        <FormField label="Destino">
                            <div className="join">
                                <button type="button" className={`btn btn-sm join-item ${!byName ? 'btn-primary' : 'btn-ghost border-base-300'}`} onClick={() => set({ stageName: null })}>
                                    Etapa específica
                                </button>
                                <button type="button" className={`btn btn-sm join-item ${byName ? 'btn-primary' : 'btn-ghost border-base-300'}`} onClick={() => set({ stageName: '', pipelineId: null, stageId: null })}>
                                    Pelo nome, no funil atual
                                </button>
                            </div>
                        </FormField>
                        {byName ? (
                            <FormField
                                label="Nome da etapa"
                                htmlFor={id('stage-name')}
                                required
                                hint="A oportunidade vai para a etapa com este nome no funil em que ela está. Um fluxo só atende vários funis com etapas iguais; se o funil não tiver a etapa, o passo sai por Se falhar."
                            >
                                <input
                                    id={id('stage-name')}
                                    className="input input-bordered input-sm w-full"
                                    list={id('stage-names')}
                                    placeholder="Aguardando atendimento"
                                    value={config.stageName ?? ''}
                                    onChange={e => set({ stageName: e.target.value })}
                                />
                                <datalist id={id('stage-names')}>
                                    {stageNames.map(name => (
                                        <option key={name} value={name} />
                                    ))}
                                </datalist>
                            </FormField>
                        ) : (
                            <PipelineStageSelect
                                idPrefix={id('move')}
                                refData={refData}
                                pipelineId={config.pipelineId}
                                stageId={config.stageId}
                                onChange={(pipelineId, stageId) => set({ pipelineId, stageId })}
                            />
                        )}
                    </>
                )
            }

            case 'CREATE_DEAL':
                return (
                    <>
                        <FormField label="Título" htmlFor={id('title')} optional>
                            <input id={id('title')} className="input input-bordered input-sm w-full" placeholder="Nova oportunidade" value={config.title ?? ''} onChange={e => set({ title: e.target.value })} />
                        </FormField>
                        <PipelineStageSelect
                            idPrefix={id('deal')}
                            refData={refData}
                            pipelineId={config.pipelineId}
                            stageId={config.stageId}
                            onChange={(pipelineId, stageId) => set({ pipelineId, stageId })}
                        />
                    </>
                )

            case 'ASSIGN_USER': {
                const roundRobin = config.mode === 'ROUND_ROBIN'
                return (
                    <>
                        <FormField label="Como escolher">
                            <div className="join">
                                {(['FIXED', 'ROUND_ROBIN'] as const).map(mode => (
                                    <button key={mode} type="button" className={`btn btn-sm join-item ${(config.mode ?? 'FIXED') === mode ? 'btn-primary' : 'btn-ghost border-base-300'}`} onClick={() => set({ mode })}>
                                        {mode === 'FIXED' ? 'Sempre o mesmo' : 'Rodízio'}
                                    </button>
                                ))}
                            </div>
                        </FormField>
                        {roundRobin ? (
                            <FormField label="Quem entra no rodízio" required hint="Recebe quem está há mais tempo sem receber por este passo. Usuário inativo na organização é pulado.">
                                <UserMultiSelect users={refData.users} selected={(config.userIds ?? []).map(Number)} onChange={userIds => set({ userIds })} />
                            </FormField>
                        ) : (
                            <FormField label="Responsável" htmlFor={id('user')} required>
                                <select id={id('user')} className="select select-bordered select-sm w-full" value={config.userId ?? ''} onChange={e => set({ userId: e.target.value ? Number(e.target.value) : null })}>
                                    <option value="">Selecione</option>
                                    {refData.users.map(u => (
                                        <option key={u.id} value={u.id}>
                                            {u.name}
                                        </option>
                                    ))}
                                </select>
                            </FormField>
                        )}
                    </>
                )
            }

            case 'CREATE_TASK':
                return (
                    <>
                        <FormField label="Tipo" htmlFor={id('task-type')}>
                            <select id={id('task-type')} className="select select-bordered select-sm w-full" value={config.taskType ?? 'TASK_FOLLOW_UP'} onChange={e => set({ taskType: e.target.value })}>
                                {Object.entries(TASK_TYPE_LABELS).map(([value, label]) => (
                                    <option key={value} value={value}>
                                        {label}
                                    </option>
                                ))}
                            </select>
                        </FormField>
                        <FormField label="O que fazer" htmlFor={id('task-title')} required>
                            <input
                                id={id('task-title')}
                                className="input input-bordered input-sm w-full"
                                placeholder="Ligar para {{lead.name}}"
                                value={config.title ?? ''}
                                onChange={e => set({ title: e.target.value })}
                            />
                            <VariableChips onInsert={variable => set({ title: `${config.title ?? ''}${variable}` })} />
                        </FormField>
                        <FormField label="Prazo" htmlFor={id('task-due')} hint="Contado a partir do momento em que o fluxo chega neste passo.">
                            <DurationInput id={id('task-due')} value={config.dueInMinutes ?? 60} min={0} onChange={dueInMinutes => set({ dueInMinutes })} />
                        </FormField>
                        <FormField label="Responsável" htmlFor={id('task-user')} hint="A tarefa entra na agenda pessoal dele, com lembrete no sino.">
                            <select id={id('task-user')} className="select select-bordered select-sm w-full" value={config.userId ?? ''} onChange={e => set({ userId: e.target.value ? Number(e.target.value) : null })}>
                                <option value="">Responsável pelo lead</option>
                                {refData.users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.name}
                                    </option>
                                ))}
                            </select>
                        </FormField>
                        <FormField label="Detalhes" htmlFor={id('task-description')} optional>
                            <textarea
                                id={id('task-description')}
                                className="textarea textarea-bordered w-full text-sm leading-relaxed"
                                rows={3}
                                placeholder="O que o vendedor precisa saber"
                                value={config.description ?? ''}
                                onChange={e => set({ description: e.target.value })}
                            />
                        </FormField>
                    </>
                )

            case 'ASSIGN_WALLET':
                return (
                    <FormField label="Carteira" htmlFor={id('wallet')} required hint="A carteira é do cliente: lead sem cliente vinculado não é movido.">
                        <select id={id('wallet')} className="select select-bordered select-sm w-full" value={config.walletId ?? ''} onChange={e => set({ walletId: e.target.value ? Number(e.target.value) : null })}>
                            <option value="">Selecione</option>
                            {refData.wallets.map(w => (
                                <option key={w.id} value={w.id}>
                                    {w.name}
                                </option>
                            ))}
                        </select>
                    </FormField>
                )

            case 'TAG':
                return (
                    <>
                        <FormField label="Ação">
                            <div className="join">
                                {(['add', 'remove'] as const).map(mode => (
                                    <button key={mode} type="button" className={`btn btn-sm join-item ${(config.mode ?? 'add') === mode ? 'btn-primary' : 'btn-ghost border-base-300'}`} onClick={() => set({ mode })}>
                                        {mode === 'add' ? 'Adicionar' : 'Remover'}
                                    </button>
                                ))}
                            </div>
                        </FormField>
                        <FormField label="Tag" htmlFor={id('tag')} required>
                            <input id={id('tag')} className="input input-bordered input-sm w-full" value={config.tag ?? ''} onChange={e => set({ tag: e.target.value })} />
                        </FormField>
                    </>
                )

            case 'SET_FIELD': {
                const options = config.entity === 'deal' ? UPDATE_DEAL_FIELD_OPTIONS : UPDATE_LEAD_FIELD_OPTIONS
                const hint = options.find(o => o.value === config.field)?.hint
                return (
                    <>
                        <FormField label="Onde">
                            <div className="join">
                                {(['lead', 'deal'] as const).map(entity => (
                                    <button key={entity} type="button" className={`btn btn-sm join-item ${(config.entity ?? 'lead') === entity ? 'btn-primary' : 'btn-ghost border-base-300'}`} onClick={() => set({ entity, field: '' })}>
                                        {entity === 'lead' ? 'Lead' : 'Oportunidade'}
                                    </button>
                                ))}
                            </div>
                        </FormField>
                        <FormField label="Campo" htmlFor={id('field')} required>
                            <select id={id('field')} className="select select-bordered select-sm w-full" value={config.field ?? ''} onChange={e => set({ field: e.target.value })}>
                                <option value="">Selecione</option>
                                {options.map(o => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </FormField>
                        <FormField label="Valor" htmlFor={id('value')} hint={hint ? `Formato: ${hint}` : undefined}>
                            <input id={id('value')} className="input input-bordered input-sm w-full" value={config.value ?? ''} onChange={e => set({ value: e.target.value })} />
                        </FormField>
                    </>
                )
            }

            case 'SET_TEMPERATURE':
                return (
                    <FormField label="Temperatura" htmlFor={id('temperature')} required>
                        <select id={id('temperature')} className="select select-bordered select-sm w-full" value={config.temperature ?? ''} onChange={e => set({ temperature: e.target.value })}>
                            <option value="">Selecione</option>
                            {Object.entries(LEAD_TEMPERATURE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </FormField>
                )

            case 'SET_LIFECYCLE':
                return (
                    <FormField label="Ciclo de vida" htmlFor={id('lifecycle')} required>
                        <select id={id('lifecycle')} className="select select-bordered select-sm w-full" value={config.lifecycleStatus ?? ''} onChange={e => set({ lifecycleStatus: e.target.value })}>
                            <option value="">Selecione</option>
                            {Object.entries(LEAD_LIFECYCLE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>
                                    {label}
                                </option>
                            ))}
                        </select>
                    </FormField>
                )

            case 'ADD_NOTE':
                return textArea('content', 'Nota', 'Texto registrado na linha do tempo do lead', 4)

            case 'WEBHOOK':
                return (
                    <>
                        <FormField label="URL" htmlFor={id('url')} required>
                            <input id={id('url')} className="input input-bordered input-sm w-full font-mono" placeholder="https://" value={config.url ?? ''} onChange={e => set({ url: e.target.value })} />
                        </FormField>
                        <FormField label="Método" htmlFor={id('method')}>
                            <select id={id('method')} className="select select-bordered select-sm w-full" value={config.method ?? 'POST'} onChange={e => set({ method: e.target.value })}>
                                {['POST', 'PUT', 'PATCH', 'GET'].map(m => (
                                    <option key={m}>{m}</option>
                                ))}
                            </select>
                        </FormField>
                        <FormField label="Corpo (JSON)" htmlFor={id('body')} optional hint="Aceita variáveis como {{lead.name}}.">
                            <textarea
                                id={id('body')}
                                className="textarea textarea-bordered w-full font-mono text-xs"
                                rows={5}
                                defaultValue={config.body !== undefined ? JSON.stringify(config.body, null, 2) : ''}
                                onBlur={e => {
                                    const raw = e.target.value.trim()
                                    if (!raw) return set({ body: undefined })
                                    try {
                                        set({ body: JSON.parse(raw) })
                                    } catch {
                                        e.target.classList.add('textarea-error')
                                    }
                                }}
                            />
                        </FormField>
                    </>
                )

            case 'START_FLOW':
                return (
                    <FormField label="Fluxo" htmlFor={id('flow')} required>
                        <select id={id('flow')} className="select select-bordered select-sm w-full" value={config.flowId ?? ''} onChange={e => set({ flowId: e.target.value ? Number(e.target.value) : null })}>
                            <option value="">Selecione</option>
                            {refData.flows
                                .filter(f => f.id !== flowId)
                                .map(f => (
                                    <option key={f.id} value={f.id}>
                                        {f.name}
                                        {f.active ? '' : ' (inativo)'}
                                    </option>
                                ))}
                        </select>
                    </FormField>
                )

            case 'STOP':
                return <p className="text-sm text-base-content/60">O fluxo termina neste passo e a execução é marcada como concluída.</p>

            default:
                return null
        }
    }

    return (
        <aside className="flex h-full w-full flex-col bg-base-100">
            <header className={`flex items-center gap-2 px-4 py-3 ${style.header}`}>
                <span className="text-lg">{meta.icon}</span>
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{meta.label}</p>
                    <p className="truncate text-xs opacity-80">{meta.description}</p>
                </div>
                <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose} aria-label="Fechar">
                    <MdClose />
                </button>
            </header>

            <div className="flex-1 space-y-5 overflow-y-auto p-4">
                {issues.length > 0 && (
                    <div className="space-y-1.5">
                        {issues.map((issue, index) => (
                            <div key={index} className={`alert py-2 text-xs ${issue.severity === 'error' ? 'alert-error' : 'alert-warning'}`}>
                                {issue.message}
                            </div>
                        ))}
                    </div>
                )}
                {body()}
            </div>

            {node.type !== 'START' && (
                <footer className="flex items-center justify-between gap-2 border-t border-base-300 px-4 py-3">
                    <span className="text-xs text-base-content/40">Chave: {node.key}</span>
                    <div className="flex gap-2">
                        <button type="button" className="btn btn-ghost btn-sm gap-1" onClick={onDuplicate}>
                            <MdContentCopy /> Duplicar
                        </button>
                        <button type="button" className="btn btn-ghost btn-sm gap-1 text-error" onClick={onDelete}>
                            <MdDelete /> Excluir
                        </button>
                    </div>
                </footer>
            )}
        </aside>
    )
}
